"use client";

import { motion, useDragControls, useReducedMotion } from "framer-motion";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { MessageSquare, MessageSquareOff } from "lucide-react";
import { ModerationMenu } from "@/components/live/moderation-menu";
import type { HallSendOutcome } from "@/lib/live/hall";
import { FrontIcon, RollWidget } from "./roll-widget";
import { useWidgetFront } from "./roll-widget-front";
import type { RollWidgetAnchor } from "./roll-widget-slots";
import { useCornerDrag } from "./use-corner-drag";

const PANEL_WIDTH_PX = 300;
const LIST_HEIGHT_PX = 260;
const BUBBLE_SIZE_PX = 48;
const MESSAGE_MAX_LENGTH = 200;

/**
 * En avant : jamais plus large que ça, ni plus haut. Une ligne de chat se lit
 * mal au-delà de soixante-dix caractères, et la salle en colonne prend le
 * reste.
 */
const FRONT_MAX_WIDTH_PX = 800;
const FRONT_MAX_HEIGHT_PX = 560;
/** De quoi poser la fiche de l'interprète — son nom, « au piano », son état — sans la tronquer. */
const ASIDE_WIDTH_PX = 272;

/** À moins de ça du bas, on considère qu'on suit le fil (px). */
const FOLLOW_THRESHOLD_PX = 24;

export type ChatAuthor = {
  readonly id: string;
  readonly name: string;
  /**
   * `performer` et `spectator` dans un récital ; `player` dans une séance à
   * plusieurs, où tout le monde joue — ni étiquette, ni modération à proposer.
   */
  readonly role: "performer" | "spectator" | "player";
  /** En sourdine, tel que l'interprète le sait. Chez la salle, jamais posé. */
  readonly muted?: boolean;
};

/**
 * Les gestes de l'interprète, chez lui seul : fermer et rouvrir le chat,
 * retirer un message, la parole, ou une personne. Rien n'est appliqué ici —
 * chaque geste part au service, et ce qu'il a fait revient par la salle.
 */
export interface ChatModeration {
  readonly setClosed: (closed: boolean) => Promise<boolean>;
  readonly retract: (id: string) => Promise<boolean>;
  readonly mute: (identity: string, muted: boolean) => Promise<boolean>;
  readonly exclude: (identity: string) => Promise<boolean>;
}

export type ChatMessage = {
  readonly id: string;
  readonly author: ChatAuthor;
  readonly text: string;
  /** Horodatage en millisecondes. */
  readonly at: number;
};

type ChatWidgetProps = {
  readonly messages: readonly ChatMessage[];
  /** Identité de qui regarde, pour reconnaître ses propres messages. */
  readonly selfId: string;
  /**
   * Envoie un message, et dit ce qu'il est devenu. Le message n'est pas
   * ajouté ici : il revient par la salle, comme chez tout le monde, et c'est
   * ce qui dit qu'il est passé.
   */
  readonly onSend: (text: string) => Promise<HallSendOutcome>;
  /** Personnes dans la salle, soi compris. Omis s'il n'est pas connu. */
  readonly audience?: number;
  readonly initialAnchor?: RollWidgetAnchor;
  /** Réduit en bulle tant que personne n'y a touché : chez qui joue, sur un téléphone. */
  readonly collapsedByDefault?: boolean;
  /**
   * La salle, en colonne à gauche du fil quand le widget est mis en avant.
   * Absente, la mise en avant n'agrandit que le fil.
   */
  readonly aside?: ReactNode;
  /** Le widget est mis en avant, ou en sort : c'est là que la salle se relève. */
  readonly onAhead?: (ahead: boolean) => void;
  /** L'interprète a fermé le chat à la salle. Lu dans l'état de séance. */
  readonly closed?: boolean;
  /** L'interprète nous a retiré la parole. */
  readonly muted?: boolean;
  /** Chez l'interprète seul. Voir {@link ChatModeration}. */
  readonly moderation?: ChatModeration;
  /** Le nom du fil pour un lecteur d'écran : celui d'un récital, par défaut. */
  readonly ariaLabel?: string;
};

/**
 * Le fil de discussion d'un récital, posé sur le roll.
 *
 * # Pourquoi aucun nom en couleur
 *
 * Les chats colorent leurs pseudos ; ici ce serait peindre du texte dans les
 * teintes qui, sur le roll juste derrière, disent quelle main joue. Les noms
 * restent achromatiques, et l'interprète — le seul qu'il faille repérer d'un
 * coup d'œil — porte une étiquette plutôt qu'une teinte.
 *
 * # Le fil ne tire pas la lecture
 *
 * Il descend tout seul tant qu'on est en bas. Si l'on remonte lire un message,
 * les nouveaux arrivent sans déplacer ce qu'on lit, et une pastille propose de
 * revenir en bas.
 *
 * # Glisser, réduire, mettre en avant — comme la vidéo
 *
 * On le déplace par son en-tête — et seulement par lui : le reste du panneau
 * sert à faire défiler, sélectionner et taper. Réduit, il devient une bulle qui
 * compte ce qu'on n'a pas lu. Mis en avant, il prend le centre du rouleau avec
 * la salle en colonne à sa gauche : qui est là, et ce qui se dit, sur un seul
 * écran, entre deux morceaux. Jamais en bulle au premier plan, et le tirer l'en
 * sort — les mêmes gestes que la vignette vidéo.
 *
 * # Ce qui ne s'affiche pas tout de suite
 *
 * Son propre message. Il part au service et revient par la salle, chez soi
 * comme chez les autres, cent à trois cents millisecondes plus tard : c'est ce
 * qui dit qu'il est passé. Un refus, lui, se dit sur-le-champ, et le texte
 * reste dans le champ pour être renvoyé.
 *
 * # La modération, chez l'interprète
 *
 * Un bouton dans l'en-tête ferme le chat à la salle — lui y parle encore,
 * c'est à ça que ça sert — et un menu sur chaque ligne de la salle retire le
 * message, la parole, ou la personne. Chez la salle, le champ se tait d'un
 * mot quand le chat est fermé ou la parole retirée : pas de champ grisé qu'on
 * ne comprend pas.
 */
export function ChatWidget({
  messages,
  selfId,
  onSend,
  audience,
  initialAnchor = "bottom-left",
  collapsedByDefault = false,
  aside,
  onAhead,
  closed = false,
  muted = false,
  moderation,
  ariaLabel = "Discussion du récital",
}: ChatWidgetProps) {
  const { anchor, boxRef, onDragEnd } = useCornerDrag(initialAnchor);
  const { front, promote, dismiss } = useWidgetFront();
  const dragControls = useDragControls();
  const reducedMotion = useReducedMotion();
  // `null` tant que personne n'a touché au panneau : c'est le défaut qui décide.
  const [choice, setChoice] = useState<boolean | null>(null);
  const collapsed = front ? false : (choice ?? collapsedByDefault);
  // Le nombre de messages vus au moment de réduire : la bulle compte le reste.
  const [seenCount, setSeenCount] = useState(0);

  useEffect(() => {
    if (onAhead === undefined) {
      return;
    }

    onAhead(front);

    return () => onAhead(false);
  }, [front, onAhead]);

  const handleCollapse = () => {
    setSeenCount(messages.length);
    setChoice(true);
    dismiss();
  };

  const startDrag = (event: PointerEvent<HTMLElement>) => {
    dragControls.start(event);
  };

  return (
    <RollWidget anchor={anchor} front={front}>
      <motion.div
        ref={boxRef}
        drag
        dragControls={dragControls}
        // Réduit, la bulle entière est la poignée ; ouvert, l'en-tête seul.
        dragListener={collapsed}
        dragMomentum={false}
        dragSnapToOrigin
        // Le tirer le sort du premier plan, vers le coin où on le lâche.
        onDragEnd={(event, info) => {
          dismiss();
          onDragEnd(event, info);
        }}
        whileDrag={{ scale: 1.02 }}
        initial={reducedMotion ? false : { opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={reducedMotion ? { duration: 0 } : { duration: 0.2, ease: "easeOut" }}
        role="region"
        aria-label={ariaLabel}
        style={front ? { maxWidth: "100%", maxHeight: "100%" } : undefined}
      >
        {collapsed ? (
          <ChatBubble
            unread={Math.max(0, messages.length - seenCount)}
            onExpand={() => setChoice(false)}
          />
        ) : (
          <ChatPanel
            messages={messages}
            selfId={selfId}
            onSend={onSend}
            audience={audience}
            ahead={front}
            aside={aside}
            closed={closed}
            muted={muted}
            moderation={moderation}
            onFront={front ? dismiss : promote}
            onCollapse={handleCollapse}
            onHeaderPointerDown={front ? undefined : startDrag}
          />
        )}
      </motion.div>
    </RollWidget>
  );
}

function ChatPanel({
  messages,
  selfId,
  onSend,
  audience,
  ahead,
  aside,
  closed,
  muted,
  moderation,
  onFront,
  onCollapse,
  onHeaderPointerDown,
}: {
  messages: readonly ChatMessage[];
  selfId: string;
  onSend: (text: string) => Promise<HallSendOutcome>;
  audience?: number;
  ahead: boolean;
  aside?: ReactNode;
  closed: boolean;
  muted: boolean;
  moderation?: ChatModeration;
  onFront: () => void;
  onCollapse: () => void;
  onHeaderPointerDown?: (event: PointerEvent<HTMLElement>) => void;
}) {
  const listRef = useRef<HTMLOListElement | null>(null);
  // Lu dans l'effet, écrit au défilement : une ref, parce que la position ne
  // change rien au rendu en elle-même — seule la pastille en dépend.
  const followingRef = useRef(true);
  const [following, setFollowing] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<Exclude<HallSendOutcome, "sent"> | null>(null);

  useLayoutEffect(() => {
    const list = listRef.current;

    if (list === null || !followingRef.current) {
      return;
    }

    list.scrollTop = list.scrollHeight;
  }, [messages.length, ahead]);

  const handleScroll = () => {
    const list = listRef.current;

    if (list === null) {
      return;
    }

    const next = list.scrollHeight - list.scrollTop - list.clientHeight < FOLLOW_THRESHOLD_PX;
    followingRef.current = next;
    setFollowing(next);
  };

  const scrollToEnd = () => {
    const list = listRef.current;

    if (list === null) {
      return;
    }

    list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = draft.trim();

    if (text === "" || sending) {
      return;
    }

    setSending(true);
    setFailure(null);

    const outcome = await onSend(text);

    setSending(false);

    if (outcome !== "sent") {
      // Le texte reste : on ne fait pas retaper ce qui n'est pas parti.
      setFailure(outcome);

      return;
    }

    setDraft("");
    // On vient d'écrire : on veut voir son message, où qu'on ait été.
    followingRef.current = true;
    setFollowing(true);
  };

  const withAside = ahead && aside !== undefined;
  // La salle se tait d'un mot ; l'interprète, lui, parle toujours.
  const silenced = moderation === undefined && (closed || muted);

  return (
    <div
      className="@container flex flex-col overflow-hidden"
      style={{
        // En avant : jamais plus étroit que dans son coin, jamais plus large
        // que la boîte ni que ce qui se lit. `cqw`/`cqh` : c'est le premier
        // plan qui est le conteneur de taille (`roll-widget-layer`).
        width: ahead ? `max(${PANEL_WIDTH_PX}px, min(100cqw, ${FRONT_MAX_WIDTH_PX}px))` : PANEL_WIDTH_PX,
        height: ahead ? `min(100cqh, ${FRONT_MAX_HEIGHT_PX}px)` : undefined,
        maxWidth: "100%",
        background: "var(--color-glass-shell)",
        backdropFilter: "blur(var(--blur-shell)) saturate(140%)",
        WebkitBackdropFilter: "blur(var(--blur-shell)) saturate(140%)",
        borderRadius: "var(--radius-shell)",
        border: "1px solid var(--color-border-subtle)",
        boxShadow: "var(--shadow-popin-focus)",
      }}
    >
      <header
        onPointerDown={onHeaderPointerDown}
        className="flex shrink-0 touch-none select-none items-center gap-2 py-2 pl-4 pr-2"
        style={{
          borderBottom: "1px solid var(--color-border-subtle)",
          cursor: onHeaderPointerDown === undefined ? undefined : "grab",
        }}
      >
        <h2
          className="font-mono text-[11px] uppercase"
          style={{ letterSpacing: "0.18em", color: "var(--color-text-secondary)" }}
        >
          Chat
        </h2>

        {audience !== undefined && (
          <span
            className="font-mono text-[11px] tabular-nums"
            style={{ color: "var(--color-text-muted)" }}
          >
            · {audience} en ligne
          </span>
        )}

        {closed && (
          <span
            className="rounded px-1.5 py-px font-mono text-[9px] uppercase leading-[1.4]"
            style={{ letterSpacing: "0.12em", color: "#fcd34d", background: "rgba(251, 191, 36, 0.14)" }}
          >
            fermé
          </span>
        )}

        <span className="flex-1" />

        {moderation !== undefined && <ClosedSwitch closed={closed} onChange={moderation.setClosed} />}

        <button
          type="button"
          onClick={onFront}
          onPointerDown={(event) => event.stopPropagation()}
          aria-label={ahead ? "Remettre dans le coin" : "Mettre en avant"}
          title={ahead ? "Remettre dans le coin" : "Mettre en avant"}
          className="grid h-6 w-6 place-items-center rounded-md text-text-muted transition-colors hover:bg-white/[0.06] hover:text-text-primary focus:outline-none focus-visible:ring-2"
          style={{ ["--tw-ring-color" as string]: "var(--color-accent)" }}
        >
          <FrontIcon ahead={ahead} />
        </button>

        {!ahead && (
          <button
            type="button"
            onClick={onCollapse}
            onPointerDown={(event) => event.stopPropagation()}
            aria-label="Réduire le chat"
            className="grid h-6 w-6 place-items-center rounded-md text-text-muted transition-colors hover:bg-white/[0.06] hover:text-text-primary focus:outline-none focus-visible:ring-2"
            style={{ ["--tw-ring-color" as string]: "var(--color-accent)" }}
          >
            <svg aria-hidden width="12" height="12" viewBox="0 0 12 12">
              <path d="M2.5 6h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </header>

      <div className="flex min-h-0 flex-1">
        {/* La salle : en colonne, et seulement quand la largeur le permet — sur
            un téléphone couché, deux colonnes n'en feraient aucune de lisible. */}
        {withAside && (
          <aside
            aria-label="La salle"
            className="hidden shrink-0 flex-col overflow-y-auto px-4 py-3 @min-[34rem]:flex"
            style={{
              width: ASIDE_WIDTH_PX,
              borderRight: "1px solid var(--color-border-subtle)",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255,255,255,0.12) transparent",
            }}
          >
            {aside}
          </aside>
        )}

        <div className="relative flex min-h-0 flex-1 flex-col">
          <ol
            ref={listRef}
            onScroll={handleScroll}
            aria-live="polite"
            aria-relevant="additions"
            className="flex min-h-0 flex-col gap-2 overflow-y-auto px-4 py-3"
            style={{
              height: ahead ? undefined : LIST_HEIGHT_PX,
              maxHeight: ahead ? undefined : "40vh",
              flex: ahead ? 1 : undefined,
              // Les messages anciens se dissolvent sous l'en-tête plutôt que d'y
              // être coupés net.
              maskImage: "linear-gradient(180deg, transparent 0, #000 20px)",
              WebkitMaskImage: "linear-gradient(180deg, transparent 0, #000 20px)",
            }}
          >
            {messages.length === 0 ? (
              <li
                className="m-auto text-center text-[12px]"
                style={{ color: "var(--color-text-muted)" }}
              >
                Aucun message pour l’instant.
              </li>
            ) : (
              messages.map((message) => (
                <ChatLine
                  key={message.id}
                  message={message}
                  mine={message.author.id === selfId}
                  moderation={moderation}
                />
              ))
            )}
          </ol>

          {!following && (
            <button
              type="button"
              onClick={scrollToEnd}
              className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[11px] transition-colors focus:outline-none focus-visible:ring-2"
              style={{
                color: "var(--color-text-primary)",
                background: "rgba(255, 255, 255, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                ["--tw-ring-color" as string]: "var(--color-accent)",
              }}
            >
              Derniers messages ↓
            </button>
          )}
        </div>
      </div>

      {silenced ? (
        <p
          role="status"
          className="m-0 shrink-0 px-4 py-3 text-[12px] leading-[1.45]"
          style={{ color: "var(--color-text-muted)", borderTop: "1px solid var(--color-border-subtle)" }}
        >
          {closed ? "L’interprète a fermé le chat." : "L’interprète t’a retiré la parole."}
        </p>
      ) : (
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex shrink-0 flex-col gap-1.5 p-2"
        style={{ borderTop: "1px solid var(--color-border-subtle)" }}
      >
        <div className="flex items-center gap-2">
          <input
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setFailure(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.currentTarget.blur();
              }
            }}
            maxLength={MESSAGE_MAX_LENGTH}
            placeholder="Écrire un message…"
            aria-label="Message"
            aria-invalid={failure !== null}
            className="min-w-0 flex-1 rounded-lg px-3 py-2 text-[13px] outline-none transition-colors placeholder:text-[var(--color-text-muted)] focus:bg-white/[0.07]"
            style={{
              color: "var(--color-text-primary)",
              background: "rgba(255, 255, 255, 0.04)",
              border: `1px solid ${failure === null ? "var(--color-border-subtle)" : "rgba(252, 165, 165, 0.5)"}`,
            }}
          />

          <button
            type="submit"
            disabled={draft.trim() === "" || sending}
            aria-label="Envoyer"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-opacity focus:outline-none focus-visible:ring-2 disabled:opacity-30"
            style={{
              color: "#ffffff",
              background: "var(--color-accent)",
              ["--tw-ring-color" as string]: "var(--color-violet-text)",
            }}
          >
            <svg aria-hidden width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M7 11.5v-9M3 6.5l4-4 4 4"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {failure !== null && (
          <p role="alert" className="m-0 px-1 text-[11px] leading-[1.4] text-red-300">
            {FAILURE_WORDS[failure]}
          </p>
        )}
      </form>
      )}
    </div>
  );
}

const FAILURE_WORDS: Record<Exclude<HallSendOutcome, "sent">, string> = {
  "too-fast": "Doucement : un message par seconde. Réessaie dans un instant.",
  // Les deux suivants ne s'affichent qu'un instant : la salle rend le fait, et
  // le champ se tait d'un mot.
  muted: "L’interprète t’a retiré la parole.",
  closed: "Le chat est fermé.",
  failed: "Le message n’est pas parti. Réessaie.",
};

/**
 * Fermer le chat à la salle, ou le rouvrir. Dans l'en-tête, chez l'interprète
 * seul. Rien n'est appliqué en attendant : le badge « fermé » vient de l'état
 * publié, comme chez tout le monde.
 */
function ClosedSwitch({ closed, onChange }: { closed: boolean; onChange: (closed: boolean) => Promise<boolean> }) {
  const [busy, setBusy] = useState(false);
  const label = closed ? "Rouvrir le chat" : "Fermer le chat à la salle";

  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void onChange(!closed).finally(() => setBusy(false));
      }}
      onPointerDown={(event) => event.stopPropagation()}
      aria-pressed={closed}
      aria-label={label}
      title={label}
      className="grid h-6 w-6 place-items-center rounded-md transition-colors hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 disabled:opacity-50"
      style={{
        color: closed ? "#fcd34d" : "var(--color-text-muted)",
        ["--tw-ring-color" as string]: "var(--color-accent)",
      }}
    >
      {closed ? (
        <MessageSquare size={13} strokeWidth={1.8} aria-hidden />
      ) : (
        <MessageSquareOff size={13} strokeWidth={1.8} aria-hidden />
      )}
    </button>
  );
}

function ChatLine({
  message,
  mine,
  moderation,
}: {
  message: ChatMessage;
  mine: boolean;
  moderation?: ChatModeration;
}) {
  const performer = message.author.role === "performer";
  // L'interprète modère la salle, pas lui-même ni un autre interprète.
  const moderable = moderation !== undefined && !mine && message.author.role === "spectator";

  return (
    <li className="group flex items-start gap-1 text-[13px] leading-[1.45]" style={{ overflowWrap: "anywhere" }}>
      <span className="min-w-0 flex-1">
        <span
          className="font-semibold"
          style={{ color: mine ? "var(--color-text-muted)" : "var(--color-text-primary)" }}
        >
          {mine ? "Toi" : message.author.name}
        </span>

        {performer && (
          <span
            className="ml-1.5 inline-block rounded px-1 py-px align-[1px] font-mono text-[9px] uppercase leading-[1.4]"
            style={{
              letterSpacing: "0.12em",
              color: "var(--color-violet-text)",
              background: "rgba(139, 92, 246, 0.16)",
            }}
          >
            Interprète
          </span>
        )}

        <span style={{ color: "var(--color-text-secondary)" }}> {message.text}</span>
      </span>

      {moderable && (
        <ModerationMenu
          target={{ identity: message.author.id, name: message.author.name, muted: message.author.muted === true }}
          gestures={moderation}
          onRetract={() => moderation.retract(message.id)}
          reveal="hover"
        />
      )}
    </li>
  );
}

function ChatBubble({ unread, onExpand }: { unread: number; onExpand: () => void }) {
  return (
    <motion.button
      type="button"
      // `onTap` : framer l'annule dès que le geste devient un glissement.
      onTap={onExpand}
      aria-label={unread > 0 ? `Ouvrir le chat, ${unread} non lus` : "Ouvrir le chat"}
      className="relative grid touch-none select-none place-items-center rounded-full focus:outline-none focus-visible:ring-2"
      style={{
        width: BUBBLE_SIZE_PX,
        height: BUBBLE_SIZE_PX,
        color: "var(--color-text-primary)",
        background: "var(--color-glass-shell)",
        backdropFilter: "blur(var(--blur-shell)) saturate(140%)",
        WebkitBackdropFilter: "blur(var(--blur-shell)) saturate(140%)",
        border: "1px solid var(--color-border-subtle)",
        boxShadow: "var(--shadow-popin-focus)",
        cursor: "grab",
        ["--tw-ring-color" as string]: "var(--color-accent)",
      }}
    >
      <svg
        aria-hidden
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 12a7.5 7.5 0 0 1-11.2 6.5L4 20l1.5-4.3A7.5 7.5 0 1 1 20 12Z" />
      </svg>

      {unread > 0 && (
        <span
          className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full px-1 text-[10px] font-semibold tabular-nums"
          style={{ color: "#ffffff", background: "var(--color-accent)" }}
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </motion.button>
  );
}
